import { useCallback } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Plus, X } from 'lucide-react'
import { clsx } from 'clsx'
import type { DraftPlayer } from '../types'
import styles from './PitchPlayerSlot.module.css'

type PitchPlayerSlotProps = {
  player:            DraftPlayer | null
  emptyPosition?:    'GK' | 'DEF' | 'MID' | 'FWD'   // band label for empty ghost slots
  emptySlotIndex?:   number           // slot index within the band — for a unique droppable id
  starterIndex?:     number           // index in draft.starters[] — enables DnD when provided
  isWindowOpen:      boolean
  isSubbedOut?:      boolean          // historical: player didn't play, was auto-replaced
  onRemove?:         (playerId: string) => void
  onClickPlayer?:    (playerId: string) => void
  onFilterPosition?: (position: 'GK' | 'DEF' | 'MID' | 'FWD') => void   // ghost "+" → filter list
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const POSITION_CLASS: Record<string, string | undefined> = {
  GK:  styles.posGk,
  DEF: styles.posDef,
  MID: styles.posMid,
  FWD: styles.posFwd,
}

// ── Component ──────────────────────────────────────────────────────────────────

export function PitchPlayerSlot({ player, emptyPosition, emptySlotIndex, starterIndex, isWindowOpen, isSubbedOut, onRemove, onClickPlayer, onFilterPosition }: PitchPlayerSlotProps) {
  // DnD is active only for filled slots when the transfer window is open.
  const dndEnabled = player !== null && starterIndex !== undefined && isWindowOpen

  // Filled slots use a STABLE, player-based id (so dnd-kit's drop animation stays correct
  // after a reorder). Empty ghost slots keep a position id and are drop targets too, so a
  // bench player of ANY position can be dragged straight into the XI (they land in their
  // own band — see useSquadDnd).
  const emptyDroppable = player === null && isWindowOpen && emptyPosition !== undefined
  const dndId = player !== null
    ? `player-${player.playerId}`
    : `empty-${emptyPosition ?? 'none'}-${emptySlotIndex ?? 0}`

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id: dndId, disabled: !dndEnabled })

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dndId,
    disabled: player !== null ? !dndEnabled : !emptyDroppable,
  })

  // Merge both refs onto the same element (filled slots are draggable + droppable).
  const setRef = useCallback((node: HTMLDivElement | null) => {
    setDragRef(node)
    setDropRef(node)
  }, [setDragRef, setDropRef])


  if (!player) {
    // Window open + a known band → the ghost circle becomes a drop target AND a "+" that
    // filters the player list to that position. Locked/historical view shows a static ghost.
    const canFilter = isWindowOpen && emptyPosition !== undefined && onFilterPosition !== undefined
    return (
      <div ref={setDropRef} className={clsx(styles.slot, isOver && styles.isOver)}>
        <div className={clsx(styles.circleEmpty, emptyPosition && styles[`ghost${emptyPosition}`])}>
          {canFilter && (
            <button
              type="button"
              className={styles.ghostAddBtn}
              aria-label={`Filtrar ${emptyPosition} disponíveis`}
              onClick={() => onFilterPosition(emptyPosition)}
            >
              <Plus size={18} strokeWidth={2.5} />
            </button>
          )}
        </div>
        <span className={clsx(styles.emptyLabel, emptyPosition && styles[`ghost${emptyPosition}`])}>
          {emptyPosition ?? '+'}
        </span>
      </div>
    )
  }

  const posClass  = POSITION_CLASS[player.position]
  const canRemove = isWindowOpen && onRemove !== undefined

  return (
    <div
      ref={setRef}
      className={clsx(
        styles.slot,
        player.isEliminated && styles.isEliminated,
        isSubbedOut          && styles.isSubbedOut,
        isDragging            && styles.isDragging,
        isOver                && styles.isOver,
        dndEnabled            && styles.canDrag,
        onClickPlayer         && styles.clickable,
      )}
      aria-label={player.displayName}
      onClick={onClickPlayer ? () => onClickPlayer(player.playerId) : undefined}
      {...(dndEnabled ? attributes : {})}
      {...(dndEnabled ? listeners  : {})}
    >
      <div className={clsx(styles.circle, posClass)}>
        {canRemove && (
          <button
            className={styles.removeBtn}
            aria-label={`Remover ${player.displayName}`}
            onPointerDown={e => e.stopPropagation()}   // don't trigger drag on remove tap
            onClick={e => { e.stopPropagation(); onRemove(player.playerId) }}
          >
            <X size={10} strokeWidth={3} />
          </button>
        )}

        <span className={styles.posLabel}>{player.position}</span>
      </div>
      <span className={styles.playerName} title={player.name}>
        {player.displayName}
      </span>
      {!isWindowOpen && player.gwPoints !== null && (
        <span className={styles.gwPoints}>{player.gwPoints} pts</span>
      )}
      {player.isEliminated && (
        <span className={styles.elimBadge} aria-label="Eliminado">ELI</span>
      )}
      {isSubbedOut && !player.isEliminated && (
        <span className={clsx(styles.subBadge, styles.subOutBadge)} aria-label="Substituído">↓</span>
      )}
    </div>
  )
}
